import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { buildAPIUrl } from "../../Store/APIStore";

export const OptimizedImage = ({
  src,
  alt,
  className = "",
  size = "medium",
  lazy = true,
  placeholder = null,
  onLoad = null,
  onError = null,
  style = {},
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(!lazy);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef();
  const observerRef = useRef();

  // Backend URL - adjust this to your backend URL
  const BACKEND_URL = buildAPIUrl();

  // Helper function to get optimized image sources
  const getImageSources = (imageSrc, size) => {
    // If src is already an optimized object from the API, use it directly
    if (imageSrc && typeof imageSrc === "object") {
      // Handle carouselOptimized format from API
      if (imageSrc.original && imageSrc.thumbnail) {
        return {
          webp: imageSrc.original.webp,
          fallback: imageSrc.original.fallback,
          thumbnail: {
            webp: imageSrc.thumbnail.webp,
            fallback: imageSrc.thumbnail.fallback,
          },
        };
      }

      // Handle stock photo optimized_urls format from API
      if (imageSrc.original && imageSrc.thumbnails) {
        const thumbnailSize =
          imageSrc.thumbnails[size] || imageSrc.thumbnails.medium;
        return {
          webp: imageSrc.original.webp,
          fallback: imageSrc.original.fallback,
          thumbnail: {
            webp: thumbnailSize.webp,
            fallback: thumbnailSize.fallback,
          },
        };
      }
    }

    // Legacy support: If it's a string URL, construct optimized versions
    const imagePath =
      typeof imageSrc === "string"
        ? imageSrc
        : imageSrc?.fallback || imageSrc?.original?.fallback;

    if (!imagePath) {
      return {
        webp: null,
        fallback: null,
        thumbnail: { webp: null, fallback: null },
      };
    }

    // If it's already a full URL, use as-is
    if (imagePath.startsWith("http") || imagePath.startsWith("/media/")) {
      const basePath = imagePath.replace(/\.[^/.]+$/, ""); // Remove extension
      return {
        webp: `${BACKEND_URL}${basePath}.webp`,
        fallback: `${BACKEND_URL}${basePath}.jpg`,
        thumbnail: {
          webp: `${BACKEND_URL}/media/photos/thumbnails/${size}/${basePath.split("/").pop()}.webp`,
          fallback: `${BACKEND_URL}/media/photos/thumbnails/${size}/${basePath.split("/").pop()}.jpg`,
        },
      };
    }

    // For relative paths, construct full URLs
    const filename = imagePath.split("/").pop();
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");

    return {
      webp: `${BACKEND_URL}/media/photos/${nameWithoutExt}.webp`,
      fallback: `${BACKEND_URL}/media/photos/${nameWithoutExt}.jpg`,
      thumbnail: {
        webp: `${BACKEND_URL}/media/photos/thumbnails/${size}/${nameWithoutExt}.webp`,
        fallback: `${BACKEND_URL}/media/photos/thumbnails/${size}/${nameWithoutExt}.jpg`,
      },
    };
  };

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || isInView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      },
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [lazy, isInView]);

  const handleLoad = () => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  };

  const handleError = () => {
    setHasError(true);
    if (onError) onError();
  };

  if (!isInView) {
    return (
      <div
        ref={imgRef}
        className={`${className} bg-gray-200 animate-pulse`}
        style={{ aspectRatio: "16/9", ...style }}
      >
        {placeholder && placeholder}
      </div>
    );
  }

  const sources = getImageSources(src, size);

  // Early return if no valid image sources
  if (!sources.thumbnail.fallback && !sources.fallback) {
    return (
      <div
        ref={imgRef}
        className={`${className} bg-gray-200 flex items-center justify-center`}
        style={{ aspectRatio: "16/9", ...style }}
      >
        <svg
          className="w-8 h-8 text-gray-400"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    );
  }

  return (
    <div ref={imgRef} className={`relative ${className}`}>
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
          {placeholder || (
            <svg
              className="w-8 h-8 text-gray-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
      )}

      <picture>
        {sources.thumbnail.webp && (
          <source
            srcSet={`${sources.thumbnail.webp} 1x${sources.webp ? `, ${sources.webp} 2x` : ""}`}
            type="image/webp"
          />
        )}
        <img
          src={
            hasError
              ? sources.fallback || sources.thumbnail.fallback
              : sources.thumbnail.fallback || sources.fallback
          }
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={`${isLoaded ? "opacity-100" : "opacity-0"} transition-opacity duration-300`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: style?.objectFit || "cover",
          }}
          {...props}
        />
      </picture>
    </div>
  );
};

OptimizedImage.propTypes = {
  src: PropTypes.oneOfType([
    PropTypes.string, // Legacy: plain image URL
    PropTypes.object, // New: carouselOptimized or optimized_urls object from API
  ]).isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  lazy: PropTypes.bool,
  placeholder: PropTypes.node,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
  style: PropTypes.object,
};

/**
 * Usage Examples:
 *
 * 1. With new carouselOptimized data from puppy API:
 * <OptimizedImage
 *   src={puppy.carouselOptimized[0]}
 *   alt={puppy.cardHeaderTitle}
 *   size="medium"
 * />
 *
 * 2. With stock photo optimized_urls from stock photos API:
 * <OptimizedImage
 *   src={stockPhoto.optimized_urls}
 *   alt={stockPhoto.alt_text}
 *   size="large"
 * />
 *
 * 3. Legacy support with plain URL (will construct optimized versions):
 * <OptimizedImage
 *   src="/media/puppies/penny_1.jpg"
 *   alt="Penny"
 *   size="small"
 * />
 *
 * Available sizes: 'small' (150px), 'medium' (300px), 'large' (600px)
 * Component automatically handles WebP + JPEG fallback for browser compatibility
 */
